import { Client } from './Client';
import { Paged } from './Paged';
import { CurrentStage } from './Stages';
import {
  CompanyDto,
  DwellingType,
  EmployeeDto,
  LicenseeDto,
  ReferralSourceDto,
} from 'app/shared-components/Types/Utils';
import { ActivityTypeObject } from '@/app/shared-components/Types/Activity';

export interface Order {
  currentStep: any;
  id: number;
  dwellingType: DwellingType;
  renewal: boolean;
  name: null | string;
  email: string;
  phoneNumber: string;
  stage: CurrentStage;
  closeDate: Date | null;
  moveInDate: Date | null;
  isMoveInDateConfirmed?: boolean | null;
  moveInDateConfirmedAt?: string | null;
  isCloseDateConfirmed?: boolean | null;
  closeDateConfirmedAt?: string | null;
  updatedAt: Date;
  createdAt: Date | null;
  completedAt: Date | null;
  processedAt: Date | null;
  referralCompany?: CompanyDto;
  subCompanyName?: string;
  parentCompanyName?: string;
  referralPartner?: EmployeeDto;
  address: Address;
  reason: null | string;
  projectedRevenue?: number;
  collectedRevenue?: number | string;
  source: string;
  salesRepresentative?: EmployeeDto;
  isSalesRepAIAgent?: boolean;
  accountManager?: EmployeeDto;
  activitiesCount: number;
  link_source: null;
  initial_research: boolean;
  contact_type: null;
  is_flagged: boolean;
  researcherName: string | null;
  processorName: string | null;
  client: Partial<Client>;
  licensee?: LicenseeDto;
  initialComment: string;
  possibleDuplications: PossibleDuplications[];
  nextActivityTime?: Date;
  nextActivityType?: ActivityTypeObject;
  last?: string;
  tags: string[];
  processor: EmployeeDto;
  subscription: string;
  esiId?: EsiId | null;
  servicesCount: number;
  cancellationReason?: CancellationReason | null;
  externalSource?: string;
  followUpActivity?: string;
  research: Research;
  orderReferralSource: ReferralSourceDto;
  isUrgent: boolean;
  useAI?: boolean;
  completedSubscriptions: number;
  pendingSubscriptions: number;
  processingNotes?: string;
  aiProcessState?: AIProcessState;
  aiIsEscalated?: boolean | null;
  nextScheduledCallTime?: Date | null;
  isCallScheduled?: boolean;
}

export interface AIProcessState {
  isCallSchedulingEnabled: boolean;
  isCallSchedulingEscalated: boolean;
  callSchedulingEscalatedAt?: Date | null;
  callSchedulingEscalationReason?: string | null;
}

export type Research = {
  status: ResearchStatus;
  message: string;
  researchedAt: string;
  researched?: ResearchStatusItem[];
};

export interface ResearchStatusItem {
  isSuccess: boolean;
  name: string;
  warnings: string[];
  error: string | null;
  completedAt?: string;
}

export enum ResearchStatus {
  Pending = 1,
  Completed = 2,
  Failed = 3,
}

export interface OrdersResult extends Paged<Order> {
  totalRevenue?: number;
  totalCommitted?: number;
  totalCollected?: number;
}

export interface PossibleDuplications {
  id: number;
  duplicatedBy: string[];
  createdAt: Date;
  stage: CurrentStage;
}

export interface OrderFormInputs {
  location: string | null;
  streetAddress: string | null;
  apartmentNumber: string | null;
  city: string | null;
  zipCode: string | null;
  state: string | null;
  referralCompanyId: string;
  referralPartnerId: string;
  salesRepresentativeId: string;
  closeDate: Date;
  moveInDate: Date;
  initialComment: string;
  dwellingType: number;
  latitude: number;
  longitude: number;
}

export interface CreateOrderResponseResult {
  id: string;
}
export interface CreateClientAndOrderRequestPayload {
  clientData?: Partial<Client>;
  orderData?: Partial<OrderDataType>;
}

// TODO - this is a duplicate of the OrderFormInputs interface in src\app\shared-components\Types\Orders.ts
export interface OrderDataType {
  /** @format date-time */
  closeDate: Date;
  /** @format date-time */
  moveInDate: Date;
  /** @format uuid */
  referralPartnerId: string | null;

  referralCompanyId: string | null;
  externalSource: string | null;
  /** @format uuid */
  salesRepresentativeId: string | null;
  processorId: string | null;
  accountManagerId: string | null;
  address: Address;
  dwellingType: DwellingType;
  initialComment: string | null;
  useAI?: boolean;
}

export interface Address {
  street?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
  apartmentNumber?: string | null;
  coordinate: Coordinate | null;
  full?: string;
  isConfirmed?: boolean | null; // null=not confirmed, true=correct, false=incorrect
  confirmedAt?: string | null; // ISO 8601 timestamp
}

export interface Coordinate {
  latitude: number;
  longitude: number;
}

export interface EsiId {
  id?: string | null;
  isManual?: boolean | null;
  isDeEnergized?: boolean | null;
}

export interface CancellationReason {
  reason?: string;
  cancelledAt?: string;
  cancelledBy?: EmployeeDto;
}

export enum UrgentStatus {
  Urgent = 1,
  'Not Urgent' = 2,
}

export interface GetAddressCandidatesRequest {
  orderId?: number;
  selectedAddress: {
    line1: string;
    line2: string;
    city: string;
    state: string;
    postalCode: string;
    locationKey: string;
  };
}

export interface SubmitAddressCandidateResponse {
  success: boolean;
  message?: string;
}
